import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiChevronDown, FiChevronUp, FiHelpCircle } = FiIcons;

const FAQSection = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  const faqs = [
    {
      question: "Do I need technical knowledge to use FunnelsDone?",
      answer: "Not at all! FunnelsDone is designed for busy MSME owners who don't have time for complex technical setups. Our drag-and-drop interface is so simple that you can create professional funnels in minutes. Plus, we provide complete onboarding support and training to get you started immediately."
    },
    {
      question: "How soon can I get results with FunnelsDone?",
      answer: "Most of our MSME clients start seeing leads within 24-48 hours of setting up their first funnel. The WhatsApp automation begins working immediately, and you can start booking appointments and getting inquiries right away. Full ROI typically shows within 30-90 days depending on your industry and implementation."
    },
    {
      question: "Can I integrate FunnelsDone with my existing tools?",
      answer: "Yes! FunnelsDone integrates seamlessly with popular tools like Google Sheets, payment gateways, email platforms, and more. However, most clients find they can replace 80% of their existing tools with FunnelsDone's all-in-one platform, saving significant money on multiple subscriptions."
    },
    {
      question: "Is support included? What if I get stuck?",
      answer: "Absolutely! We provide 24/7 support via WhatsApp, email, and live chat. You also get access to our comprehensive training library, weekly group coaching calls, and our private community of successful MSME owners. We're committed to your success - not just selling you software."
    },
    {
      question: "Can my team access the FunnelsDone dashboard?",
      answer: "Yes! You can add unlimited team members with different permission levels. Your sales team can access the CRM, your marketing team can manage campaigns, and you maintain full admin control. Everyone works from the same unified platform, improving collaboration and accountability."
    },
    {
      question: "Can this work for small businesses with limited budgets?",
      answer: "FunnelsDone is specifically designed for MSMEs and small businesses. By replacing 15+ expensive tools with one affordable platform, you actually save money while getting more functionality. We also offer flexible payment plans and our ROI typically covers the investment within the first month."
    },
    {
      question: "What about data security and privacy?",
      answer: "Your data security is our top priority. FunnelsDone uses bank-level encryption, regular backups, and complies with all Indian data protection regulations. Your customer data, business information, and communications are completely secure and private."
    },
    {
      question: "How is this different from other marketing tools?",
      answer: "Unlike generic marketing tools built for Western markets, FunnelsDone is specifically designed for Indian MSMEs. We understand your challenges - WhatsApp communication, local payment methods, Hindi language support, and the unique needs of manufacturing, services, and trading businesses."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <section className="py-20 bg-gray-50 relative overflow-hidden">
      <div className="container mx-auto px-6">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
            <SafeIcon icon={FiHelpCircle} className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Frequently Asked 
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600"> Questions</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Get answers to common questions about FunnelsDone and how it can transform your MSME business
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              className="mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-gray-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
                >
                  <h3 className="text-lg font-semibold text-gray-900 pr-8">{faq.question}</h3>
                  <div className="flex-shrink-0">
                    <SafeIcon 
                      icon={openFAQ === index ? FiChevronUp : FiChevronDown} 
                      className="w-6 h-6 text-gray-500"
                    />
                  </div>
                </button>

                <AnimatePresence>
                  {openFAQ === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-8 pb-6 border-t border-gray-100">
                        <p className="text-gray-700 leading-relaxed pt-4">{faq.answer}</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA after FAQ */}
        <motion.div 
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white max-w-3xl mx-auto">
            <h3 className="text-2xl font-bold mb-4">Still Have Questions?</h3>
            <p className="text-lg mb-6 opacity-90">
              Book a free strategy call and get all your questions answered by our MSME growth experts
            </p>
            <a 
              href="https://funnelsdone.com/cash" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-bold text-lg rounded-full hover:bg-gray-100 transition-all duration-300"
            >
              👉 Get Your Questions Answered
              <SafeIcon icon={FiHelpCircle} className="ml-2 w-5 h-5" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;